import axios, {
  AxiosError,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from "axios";
import * as Sentry from "@sentry/react";
import {
  getAccessToken,
  getActorId,
  getAdminSessionStash,
  getPasswordExpiredToken,
  getPreAuthToken,
  getRefreshToken,
  setAccessTokenStorage,
  setActorIdStorage,
  setAdminSessionStash,
  setPasswordExpiredTokenStorage,
  setPreAuthTokenStorage,
  setRefreshTokenStorage,
} from "./config/utils";
import { acquireMsalToken } from "./config/msalTokenProvider";

export const PSP_API_BASE_URL = `${import.meta.env.VITE_BASE_URL}/api`;

const API = axios.create({
  baseURL: PSP_API_BASE_URL,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: AxiosError) => void;
}> = [];

const processQueue = (
  error: AxiosError | null,
  token: string | null = null,
) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token!);
  });
  failedQueue = [];
};

function resetAuthLocalStorage() {
  setAccessTokenStorage(null);
  setPreAuthTokenStorage(null);
  setPasswordExpiredTokenStorage(null);
  setRefreshTokenStorage(null);
  setActorIdStorage(null);
  setAdminSessionStash(null);
}

// the session is unrecoverable (no/expired refresh token). If it was an
// impersonation session, fall back to the stashed admin session instead of
// stranding the admin inside someone else's account.
function endSessionOrRestoreAdmin() {
  const adminSession = getActorId() ? getAdminSessionStash() : null;

  if (adminSession) {
    setAccessTokenStorage(adminSession.accessToken);
    setRefreshTokenStorage(adminSession.refreshToken);
    setActorIdStorage(null);
    setAdminSessionStash(null);
    window.location.href = "/admin/users";
    return;
  }

  resetAuthLocalStorage();
  window.location.href = "/login";
}

// requests
API.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token =
      getAccessToken() || getPasswordExpiredToken() || getPreAuthToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// responses
API.interceptors.response.use(
  (response) => response,
  async (
    err: AxiosError & { config?: AxiosRequestConfig & { _retry?: boolean } },
  ) => {
    const originalRequest = err.config!;
    const status = err.response?.status;
    const refreshToken = getRefreshToken();

    if (status !== 401 || originalRequest._retry || !refreshToken) {
      if (status !== 401) {
        Sentry.captureException(err, {
          tags: {
            source: "api",
            url: err.config?.url,
            method: err.config?.method,
            status: String(status ?? "network_error"),
          },
        });
      }

      // no refresh token?
      // really bad, the session is dead
      if (!refreshToken && window.location.pathname !== "/login") {
        endSessionOrRestoreAdmin();
      }
      return Promise.reject(err);
    }

    // Queue this request if a refresh is already in flight
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest._retry = true; // prevent re-queuing on retry
          originalRequest.headers!["Authorization"] = `Bearer ${token}`;
          return API(originalRequest);
        })
        .catch((queueError) => Promise.reject(queueError));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { data } = await axios.post(
        `${PSP_API_BASE_URL}/auth/refresh`,
        null,
        { headers: { Authorization: `Bearer ${refreshToken}` } },
      );

      if (!parseAuthResponse(data)) {
        throw new Error("Auth response has wrong shape.");
      }

      setAccessTokenStorage(data.accessToken);
      setRefreshTokenStorage(data.refreshToken);
      setActorIdStorage(data.actorId);

      API.defaults.headers.common["Authorization"] =
        `Bearer ${data.accessToken}`;

      processQueue(null, data.accessToken);

      originalRequest.headers!["Authorization"] = `Bearer ${data.accessToken}`;
      return API(originalRequest);
    } catch (refreshError) {
      const axiosRefreshError = refreshError as AxiosError;
      processQueue(axiosRefreshError, null);

      if (
        axiosRefreshError.response?.status === 401 ||
        axiosRefreshError.response?.status === 403
      ) {
        // the refresh token is dead (expired, or the 1-hour impersonation
        // cap was hit) — treat it the same as an implicit "return to admin"
        endSessionOrRestoreAdmin();
      } else if (axiosRefreshError.response?.status === 435) {
        try {
          const idToken = await acquireMsalToken();
          const { data } = await axios.post(
            `${PSP_API_BASE_URL}/auth/login_sso`,
            { token: idToken },
          );
          if (!parseAuthResponse(data)) {
            throw new Error("login_sso response is not of the expected type.");
          }
          setAccessTokenStorage(data.accessToken);
          setRefreshTokenStorage(data.refreshToken);
          setActorIdStorage(data.actorId);

          API.defaults.headers.common["Authorization"] =
            `Bearer ${data.accessToken}`;

          processQueue(null, data.accessToken);
          originalRequest.headers!["Authorization"] =
            `Bearer ${data.accessToken}`;
          return API(originalRequest);
        } catch {
          resetAuthLocalStorage();
          window.location.assign("/sso-login");
        }
      } else {
        Sentry.captureException(refreshError, {
          tags: { source: "token_refresh" },
        });
      }

      return Promise.reject(axiosRefreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

type AuthResponse = {
  accessToken: string;
  refreshToken: string;
  actorId?: string | null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseAuthResponse(response: any): response is AuthResponse {
  if ("accessToken" in response && "refreshToken" in response) {
    return true;
  }
  return false;
}

export default API;
